import classNames from 'classnames/bind';
import throttle from 'lodash.throttle';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Image from '@client/components/image/Image';
import FooterSocialIcon from '@client/containers/appFrame/footer/footerSocialIcon/FooterSocialIcon';
import ReactLink from '@client/components/reactLink/ReactLink';
import NavBar from '@client/containers/navBar/NavBar';
import SideMenu from '@client/containers/sideMenu/SideMenu';
import { strings } from '@client/localization/Localization';
import { categoryById, ROUTES } from '@client/pages/routes';
import { getLoadedCategories } from '@client/redux/dataFetching/fetchDataReducer';
import { HEADER_TRANSITION_Y } from '@client/shared/js/constants';
import indicators from '@client/shared/js/indicators';
import logo from '@client/shared/js/logo';
import themeStyles from '@client/shared/styles/theme/theme-styles.scss';
import styles from './mainHeader.scss';

const navBarEntries = {
  data: [
    {
      title: `${strings.episodes}`,
      route: `${ROUTES.episodes}`,
    },
    {
      title: `${strings.series}`,
      route: `${ROUTES.series}`,
    },
    {
      title: `${strings.categories}`,
      route: undefined,
    },
  ],
};

class MainHeader extends PureComponent {
  constructor(props) {
    super(props);

    const headerTransitionEnabled = this.headerTransitionEnabled();

    this.state = {
      sideMenuIsOpen: false,
      solidHeaderBackground: !headerTransitionEnabled,
      headerTransitionEnabled: headerTransitionEnabled,
    };

    this.scrollListener = throttle(this.handleScroll.bind(this), 50);
  }

  componentDidMount() {
    window.addEventListener('scroll', this.scrollListener);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.scrollListener);
  }

  componentDidUpdate() {
    const headerTransitionEnabled = this.headerTransitionEnabled();
    if (headerTransitionEnabled !== this.state.headerTransitionEnabled) {
      this.setState({
        solidHeaderBackground: !headerTransitionEnabled,
        headerTransitionEnabled: headerTransitionEnabled,
      });
    }
  }

  getStickyHeaderClassNames() {
    return classNames({
      [styles.stickyHeader]: true,
      [styles.stickyHeaderTransitionDark]: this.state.solidHeaderBackground,
    });
  }

  handleScroll() {
    if (!this.state.headerTransitionEnabled) {
      return;
    }

    const pageYOffset = window.pageYOffset;

    if (
      pageYOffset >= HEADER_TRANSITION_Y &&
      !this.state.solidHeaderBackground
    ) {
      this.setState({
        solidHeaderBackground: true,
      });
    } else if (
      pageYOffset < HEADER_TRANSITION_Y &&
      this.state.solidHeaderBackground
    ) {
      this.setState({
        solidHeaderBackground: false,
      });
    }
  }

  toggleSideMenu = () => {
    const menuToggle = this.state.sideMenuIsOpen;
    this.setState({
      sideMenuIsOpen: !menuToggle,
    });
  };

  headerTransitionEnabled = () => {
    const { pathname } = this.props.location;
    return pathname.indexOf('admin') === -1 && pathname.indexOf('login') === -1;
  };

  render() {
    const { sideMenuIsOpen } = this.state;
    const { location, categories } = this.props;

    const nonEmptyCategories = categories.filter(
      (category) => category.episodeCount > 0
    );

    const subEntries =
      (nonEmptyCategories && {
        [strings.categories]: nonEmptyCategories.map((category) => {
          return {
            name: category.title,
            route: categoryById(category.slug),
          };
        }),
      }) ||
      [];

    return (
      <header className={styles.mainHeader}>
        <div className={this.getStickyHeaderClassNames()}>
          <div className={styles.headerContent}>
            <div className={styles.logoAndIconsContainer}>
              <ReactLink to={ROUTES.home}>
                <div className={styles.headerTitle}>
                  <Image className={styles.logo} src={logo.logo_yellow_2x} />
                </div>
              </ReactLink>
              <div
                className={`${styles.socialIcons} ${themeStyles.hideOnMobile}`}
              >
                <FooterSocialIcon
                  className={styles.footerSocialIcon}
                  route={'https://www.instagram.com/watchfactuals/?hl=en'}
                  socialName="instagram"
                />
                <FooterSocialIcon
                  className={styles.footerSocialIcon}
                  route={'https://www.facebook.com/FactualsCom/'}
                  socialName="facebook"
                />
                <FooterSocialIcon
                  className={styles.footerSocialIcon}
                  route={
                    'https://www.linkedin.com/company/factuals-nordic/?viewAsMember=true'
                  }
                  socialName="linkedin"
                />
                <FooterSocialIcon
                  className={styles.footerSocialIcon}
                  route={
                    'https://www.youtube.com/channel/UC3IVjalqPzQda3FOuSdGnSA?view_as=subscriber'
                  }
                  socialName="you-tube"
                />
              </div>
            </div>
            <div className={styles.navigationMenu}>
              <NavBar
                className={`${styles.navBar} ${themeStyles.hideOnMobile}`}
                entries={navBarEntries.data}
                subEntries={subEntries}
                location={location}
              />
              <Image
                className={`${styles.navBarButtonIcon} ${themeStyles.hideOnMobile}`}
                src={indicators.icon_menu}
                onClick={this.toggleSideMenu}
              />
              <i
                className={`icomoon icon-burgermenu_icon ${styles.hamburgerIconMobile} ${themeStyles.hideOnDesktop}`}
                onClick={this.toggleSideMenu}
              />
              {sideMenuIsOpen && (
                <SideMenu
                  onCloseClick={this.toggleSideMenu}
                  location={location}
                  subEntries={subEntries}
                />
              )}
            </div>
          </div>
        </div>
      </header>
    );
  }
}

MainHeader.propTypes = {
  location: PropTypes.object,
  categories: PropTypes.array,
};

function mapStateToProps(state) {
  return {
    categories: getLoadedCategories(state),
  };
}

export default connect(mapStateToProps)(withRouter(MainHeader));
